import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { user, login, register, t, lang, changeLang } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("welcome"));
      } else {
        await register({ email, password, name, role: "teller" });
        toast.success("Account created");
      }
      nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = detail
        ? formatErr(detail)
        : (err.code === "ERR_NETWORK" || !err.response)
          ? "Tidak dapat terhubung ke server / Cannot reach server"
          : err.message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1694018359679-49465b4c0d61?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Riyadh"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/60 to-emerald-950/70" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-slate-900 font-bold">R</div>
            <div>
              <div className="font-semibold">Riyadh-Nusantara FX</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">OTC Currency Desk</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-3">SAR → IDR → USDT</h1>
            <p className="text-slate-300 max-w-md">
              A precision OTC desk for foreign currency and stablecoin conversions. Manual & live rate override, multi-leg chain conversion, printable receipts, and complete audit trail.
            </p>
            <div className="flex gap-2 mt-6">
              <span className="pill bg-amber-500/15 text-amber-400 border border-amber-500/30">SAR</span>
              <span className="pill bg-rose-500/15 text-rose-400 border border-rose-500/30">IDR</span>
              <span className="pill bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">USDT</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 glass border-slate-800">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold">{mode === "login" ? t("login") : t("register")}</h2>
              <p className="text-sm text-slate-400 mt-1">{t("tagline")}</p>
            </div>
            <div className="flex items-center gap-1 border border-slate-800 rounded-full p-1">
              <button data-testid="lang-en" onClick={() => changeLang("en")} className={`text-xs px-2 py-0.5 rounded-full ${lang === "en" ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-400"}`}>EN</button>
              <button data-testid="lang-id" onClick={() => changeLang("id")} className={`text-xs px-2 py-0.5 rounded-full ${lang === "id" ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-400"}`}>ID</button>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label>{t("name")}</Label>
                <Input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" />
              </div>
            )}
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
            </div>
            <Button data-testid="submit-btn" type="submit" disabled={busy} className="w-full bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold">
              {busy ? "…" : (mode === "login" ? t("login") : t("register"))}
            </Button>
          </form>
          <div className="mt-6 text-center text-xs text-slate-500">
            {mode === "login" ? (
              <button data-testid="switch-register" onClick={() => setMode("register")} className="hover:text-amber-400">
                No account? Create one
              </button>
            ) : (
              <button data-testid="switch-login" onClick={() => setMode("login")} className="hover:text-amber-400">
                Have an account? Sign in
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
